import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowDown, ChevronRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useContent } from "@/context/ContentContext";
import { useLang } from "@/context/LanguageContext";

export const Hero = () => {
  const { content } = useContent();
  const navigate = useNavigate();
  const { L } = useLang();
  const h = content?.hero || {};

  return (
    <section id="home" className="relative min-h-[100svh] flex items-center overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0">
        <img
          src={h.bg_image}
          alt="Surfer che cavalca un'onda del Mediterraneo a Isola delle Femmine"
          className="absolute inset-0 w-full h-full object-cover animate-slow-zoom"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ocean-900/40 via-ocean-900/25 to-ocean-900/90" />
        <div className="absolute inset-0 bg-gradient-to-r from-ocean-900/60 via-ocean-900/15 to-transparent" />
      </div>


      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 w-full pt-28 pb-16">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="eyebrow text-gold mb-5"
        >
          {L(h.eyebrow)}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mb-6"
        >
          <Logo variant="light" className="h-28 sm:h-40 lg:h-52 w-auto drop-shadow-2xl" />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="font-heading text-sand-100 text-3xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[1.02] max-w-3xl"
        >
          {L(h.slogan)}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4"
        >
          <button
            onClick={() => navigate("/book")}
            data-testid="hero-book-button"
            className="inline-flex items-center justify-center gap-2 bg-coral hover:bg-coral/90 text-white font-heading font-bold text-base sm:text-lg px-8 py-4 rounded-full transition-all hover:scale-[1.03] shadow-xl shadow-coral/25"
          >
            {L(h.cta_primary) || "PRENOTA UNA LEZIONE"} <ChevronRight className="w-5 h-5" />
          </button>
          <button
            onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="hero-discover-button"
            className="inline-flex items-center justify-center gap-2 border border-sand-100/40 text-sand-100 hover:bg-sand-100/10 font-heading font-semibold text-base sm:text-lg px-8 py-4 rounded-full backdrop-blur-sm transition-all"
          >
            {L(h.cta_secondary) || "SCOPRI SALTY FOLKS"}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.55 }}
          className="mt-14 flex flex-wrap gap-x-10 gap-y-4"
        >
          {(h.stats || []).map((s, i) => (
            <div key={i} data-testid={`hero-stat-${i}`} className="text-sand-100">
              <div className="font-heading text-xl sm:text-2xl font-bold">{L(s.value)}</div>
              <div className="font-mono text-[0.65rem] tracking-[0.2em] uppercase text-sand-100/60">{L(s.label)}</div>
            </div>
          ))}
        </motion.div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-sand-100/70 animate-bounce">
        <ArrowDown className="w-6 h-6" />
      </div>
    </section>
  );
};
